/**
 * Utility functions for results components
 */

import type { AnalysisResult } from "@/types/analysis";
import type { StudyResults, TrialResult } from "@/types/api";
import { getParameterDisplayName } from "@/utils/parameterNames";

/**
 * AIE-319: confidence-factor configuration used to convert the optimizer's
 * "strict" objective value back to an approximate "real" Sortino/Sharpe.
 *
 * The optimizer computes `strict = ratio × confidence_factor`, where
 *   confidence_factor = 1 / (1 + exp(-steepness × (ln(num_trades) − ln(target_trades))))
 *
 * `target_trades` and `sigmoid_steepness` live in the study's TOML config
 * under `[fixed_parameters]`. Production-default values (also written by
 * `frontend/src/utils/tomlGenerator.ts:64-65`) are 100 and 2.0; we use them
 * only as a fallback when a study's TOML is missing or doesn't override.
 */
export interface ApproxConfig {
  targetTrades: number;
  sigmoidSteepness: number;
}

const APPROX_DEFAULTS: ApproxConfig = {
  targetTrades: 100,
  sigmoidSteepness: 2,
};

/**
 * Extract `target_trades` and `sigmoid_steepness` from a study's TOML
 * `[fixed_parameters]` section. Falls back to production defaults for any
 * value missing or non-finite. Returns defaults when the TOML is null/empty.
 *
 * Lightweight regex parse — TOML is generated by us with a stable shape
 * (`tomlGenerator.ts`), so a full TOML parser is overkill. If the format
 * ever changes meaningfully, this and `tomlGenerator.ts` should evolve
 * together.
 */
export function parseApproxConfigFromToml(
  toml: string | null | undefined,
): ApproxConfig {
  if (!toml) return { ...APPROX_DEFAULTS };

  const targetMatch = toml.match(/^\s*target_trades\s*=\s*([\d.]+)/m);
  const steepMatch = toml.match(/^\s*sigmoid_steepness\s*=\s*([\d.]+)/m);

  const parsedTarget = targetMatch ? Number(targetMatch[1]) : NaN;
  const parsedSteep = steepMatch ? Number(steepMatch[1]) : NaN;

  return {
    targetTrades:
      Number.isFinite(parsedTarget) && parsedTarget > 0
        ? parsedTarget
        : APPROX_DEFAULTS.targetTrades,
    sigmoidSteepness:
      Number.isFinite(parsedSteep) && parsedSteep > 0
        ? parsedSteep
        : APPROX_DEFAULTS.sigmoidSteepness,
  };
}

/**
 * AIE-319: Format the strict objective value with an approximate "real" value
 * in parentheses, e.g. `0.3400 (~2.4)`.
 *
 * `config` is required positionally to force the caller to make a deliberate
 * choice. Pass a parsed `ApproxConfig` (typically from
 * `parseApproxConfigFromToml(study.toml_config)`) when the call site has
 * the study's TOML in scope. Pass `null` when it does not — the helper will
 * render strict-only (no parenthetical) rather than invent values.
 *
 * Conservative omission rules — render strict alone with no parenthetical when:
 *   - strict is null/undefined/non-finite
 *   - strict <= 0. AIE-637 made the backend stop scaling a negative ratio by the
 *     confidence factor (`calculate_robust_score` in
 *     backend/lorentzian_optimizer.py), so a non-positive strict value IS the raw
 *     ratio already — dividing by cf would fabricate a number. This also covers
 *     both sentinels: the -100.0 ratio-sanitization clamp and the -101.0
 *     INELIGIBLE_OBJECTIVE_SCORE insufficient-trades override.
 *   - totalTrades is null/undefined/<= 0/non-finite
 *   - the resulting real value is non-finite
 *   - `config` is null (caller has no TOML in scope)
 */
export function formatStrictWithApprox(
  strict: number | null | undefined,
  totalTrades: number | null | undefined,
  config: ApproxConfig | null,
  opts?: { strictDigits?: number },
): string {
  const strictDigits = opts?.strictDigits ?? 4;

  if (strict == null || !Number.isFinite(strict)) return "N/A";
  const strictStr = strict.toFixed(strictDigits);

  if (config == null) return strictStr;
  if (strict <= 0) return strictStr;
  if (
    totalTrades == null ||
    !Number.isFinite(totalTrades) ||
    totalTrades <= 0
  ) {
    return strictStr;
  }

  const logDiff = Math.log(totalTrades) - Math.log(config.targetTrades);
  const cf = 1 / (1 + Math.exp(-config.sigmoidSteepness * logDiff));
  if (!Number.isFinite(cf) || cf === 0) return strictStr;

  const real = strict / cf;
  if (!Number.isFinite(real)) return strictStr;

  return `${strictStr} (~${real.toFixed(1)})`;
}

/**
 * Format performance value to avoid showing 0.000000 for tiny values
 */
export function formatPerformanceValue(
  value: number | string | null | undefined,
): string {
  const num =
    typeof value === "number" ? value : value != null ? Number(value) : NaN;
  if (!Number.isFinite(num)) return "N/A";
  const abs = Math.abs(num);
  // Use fixed for reasonable magnitudes, scientific for very small/large
  if (abs >= 1e-4 && abs < 1e6) return num.toFixed(6);
  return num.toExponential(6);
}

/**
 * Get trial duration in seconds, normalizing field names
 */
export function getTrialDurationSeconds(
  trial: TrialResult | Record<string, unknown>,
): number | null {
  const raw =
    (trial as Record<string, unknown>)?.duration_seconds ??
    (trial as Record<string, unknown>)?.duration ??
    null;
  const num =
    typeof raw === "number" ? raw : raw != null ? Number(raw) : null;
  return Number.isFinite(num as number) ? (num as number) : null;
}

/**
 * Generate recommendations based on analysis
 */
export function generateRecommendations(
  analysis: AnalysisResult,
  results?: StudyResults,
): string[] {
  const recommendations: string[] = [];

  // Check convergence
  if (analysis.convergenceRate > 0.8) {
    recommendations.push(
      "Strong parameter convergence detected - the optimization has found a stable region",
    );
  }

  // Check parameter importance
  const topParams = analysis.parameterImportance.slice(0, 3);
  if (topParams.length > 0) {
    recommendations.push(
      `Focus on fine-tuning: ${topParams.map((p) => getParameterDisplayName(p.name)).join(", ")} - these show the highest impact`,
    );
  }

  // Check if more trials might help
  if (results?.top_trials && results.top_trials.length >= 10) {
    const lastTrialImprovement =
      analysis.performanceProgression[
        analysis.performanceProgression.length - 1
      ]!.improvement;
    if (Math.abs(lastTrialImprovement) > 5) {
      recommendations.push(
        "Performance is still improving significantly - consider running more trials",
      );
    }
  }

  return recommendations;
}

/**
 * Calculate top 10 average performance
 */
export function calculateTop10Average(results: StudyResults | undefined): string {
  if (!results?.top_trials) return "N/A";

  const vals = results.top_trials
    .map((t) => t.value)
    .filter((v): v is number => typeof v === "number");

  return vals.length
    ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(4)
    : "N/A";
}

/**
 * Calculate top 10 range (half-range for ± display)
 */
export function calculateTop10Range(results: StudyResults | undefined): string {
  if (!results?.top_trials) return "N/A";

  const vals = results.top_trials
    .map((t) => t.value)
    .filter((v): v is number => typeof v === "number");

  if (vals.length < 2) return "N/A";

  // Divide by 2 for proper ± notation (half-range)
  return ((Math.max(...vals) - Math.min(...vals)) / 2).toFixed(4);
}

/**
 * Calculate top 10 consistency percentage
 */
export function calculateTop10Consistency(results: StudyResults | undefined): number {
  if (!results?.top_trials) return 0;

  const vals = results.top_trials
    .map((t) => t.value)
    .filter((v): v is number => typeof v === "number");

  if (vals.length < 2) return 0;

  const spread = Math.max(...vals) - Math.min(...vals);
  const denom = Math.abs(results.best_value || 1);

  return Math.max(0, Math.min(100, (1 - spread / denom) * 100));
}
